import { FC, useContext, useEffect, useState } from "react";
import GlobalContext from "src/context/global-context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { useMutation } from "react-query";
import client from "../../api";
import { BankCard, PaginationInput, User } from "src/api/types";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import {
  capitalizeEachFirstLetter,
  capitalizeFirstLetter,
  convertPaymentSystem,
  convertServiceName,
  extractBillingStatus,
  extractServiceName,
  getCurrencyInfo,
  getErrorMessage,
  getServiceName,
  getTransactionStatusStyle,
  logger,
  makeNumberWithFloatingDigits,
} from "src/lib/utils";
import { Table } from "antd";
import { ColumnsType } from "antd/es/table";
import {
  BalanceDataType,
  BankCardDataType,
  IBillingTransaction,
  IKwickpayTransaction,
} from "src/lib/constants/table-type";
import { useModalAction } from "../modal-views/context";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import routes from "src/config/routes";
import { useDebounce } from "react-use";
import SafeImage from "src/hooks/validateImageUrl";

const BillingsListPage = ({
  searchText,
  searchParams,
  setSearchParams,
  onSelectedInvoiceData,
  onSelectedUser,
  searchField,
  filterValue,
  startDate,
  endDate,
  isBuisiness,
  billingFilter,
  timezone,
}: {
  searchText?: string;
  searchParams: PaginationInput;
  setSearchParams: React.Dispatch<React.SetStateAction<PaginationInput>>;
  onSelectedInvoiceData: any;
  onSelectedUser: any;
  searchField: string;
  filterValue: string;
  startDate?: Date | null | string;
  endDate?: Date | null | string;
  isBuisiness?: boolean;
  billingFilter?: string;
  timezone: string;
}) => {
  const navigate = useNavigate();

  const targetModule = isBuisiness
    ? permissionConstants.TRANSACTION_HISTORY_BUSINESS
    : permissionConstants.TRANSACTION_HISTORY_INDIVIDUAL;
  const { canView, canList, canDownload } = usePermissions(targetModule);
  const canViewTransactions = canView || canList;

  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const { openModal } = useModalAction();

  const [dataSource, setDataSource] = useState<IBillingTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    setIsMobile(window.innerWidth < 768 ? true : false);
  }

  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     if (isMounted) {
  //       logger.log("called");
  //       getList({
  //         ...searchParams,
  //         search_value: searchText.length > 0 ? searchText : undefined,
  //         search_field: searchText.length > 0 ? searchField : undefined,
  //         start_date: startDate
  //           ? moment(startDate).format("yyyy-MM-DD")
  //           : undefined,
  //         end_date: endDate ? moment(endDate).format("yyyy-MM-DD") : undefined,
  //       });
  //     }
  //   }, 500);

  //   return () => clearTimeout(timer);
  // }, [
  //   isMounted,
  //   searchParams.current_page,
  //   searchText,
  //   searchField,
  //   startDate,
  //   endDate,
  //   isBuisiness,
  // ]);

  useEffect(() => {
    setSearchParams({
      ...searchParams,
      current_page: 1,
    });
  }, [searchText, searchParams.id, billingFilter, startDate, endDate]);

  const onPageChange = (page: number, pageSize: number) => {
    logger.log(page, pageSize);
    setSearchParams({
      ...searchParams,
      current_page: page,
    });
  };

  // const onSelectUser = (userID: number) => {
  //   getUsers({
  //     per_page: LIST_PAGE_SIZE,
  //     current_page: 1,
  //     search_value: Number(userID),
  //     search_field: "user_id",
  //   });
  // };

  const onSelectUser = (userID: number) => {
    if (!canView) return;
    navigate(`/${isBuisiness ? "users-business" : "users"}/${String(userID)}`, {
      replace: true,
    });
  };

  const { mutate: getUsers, isLoading: isLoadingUsers } = useMutation(
    client.users.getUsers,
    {
      onSuccess: (data) => {
        if (data.users.length > 0) {
          onSelectedUser(data.users[0]);
        }
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    }
  );

  const { mutate: getList, isLoading } = useMutation(
    isBuisiness
      ? client.transactions.moneyMoversBuisiness
      : client.transactions.moneyMoversIndividual,
    {
      onSuccess: (data) => {
        setTotal(data.total);

        setDataSource(
          data.data.map((item) => ({
            key: item.id,
            id: item.id,
            userId: item?.user?.id,
            email: item?.user?.email,
            fullName:
              `${item?.user?.user_data?.first_name || ""} ${
                item?.user?.user_data?.last_name || ""
              }`.trim() || "__",
            transactionId: item?.mm_txn_id,
            accountNumber:
              item?.info_response?.service_params?.account ??
              item?.info_response?.service_params?.phone ??
              item?.info_response?.service_params?.personal_number ??
              "__",
            fee: item?.fee,
            serviceName: item?.serviceName,
            serviceImage: item?.service?.service_image,
            serviceCategory:
              item?.service?.service_category?.cat_name?.[2] ?? "__",
            serviceCategoryImage: item?.service?.service_category?.cat_image,
            amount: item.amount,
            benefit: item?.accountant?.benefit,
            commission: item?.accountant?.commission,
            clientCommission: item?.accountant?.client_commission,
            currency: item?.to_currency_id,
            status: item.status,
            created_at: item.created_at,
            payment_reference: item?.user?.payment_reference ?? "__",
          }))
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    }
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isMounted && canViewTransactions) {
        logger.log("called");
        const { serviceName, id, ...params } = searchParams;
        getList({
          ...(billingFilter === "status" && { status: filterValue }),
          search_value:
            searchText && searchText.length > 0 ? searchText : undefined,
          // search_field: searchText.length > 0 ? searchField : undefined,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          ...params,
          service: searchParams.id,
          timezone: timezone,
          // user_type: isBuisiness ? "business": "individual"
        });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [
    isMounted,
    canViewTransactions,
    searchParams,
    searchText,
    searchField,
    startDate,
    endDate,
    isBuisiness,
    filterValue,
  ]);

  const { mutate: downloadReceipt, isLoading: downloadReceiptLoading } =
    useMutation(client.transactions.downloadTransactionReceiptPdf, {
      onSuccess: (pdfBlob, variables) => {
        const blob = new Blob([pdfBlob], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `invoice_${variables.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();

        setAlertText("Receipt downloaded successfully");
        setIsAlertOpened(true);
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          setAlertText(
            error.response
              ? getErrorMessage(error.response.data)
              : APISomethingWrongMsg
          );
        }
        setIsAlertOpened(true);
      },
    });

  const receiptBtn = (record: any) => {
    if (!canDownload) {
      setAlertText("You do not have permission to download receipt");
      setIsAlertOpened(true);
      return;
    }
    const recordKey = typeof record === "object" ? record.key : record;
    logger.log("RECORD", record);
    setDownloadingId(recordKey);
    downloadReceipt(
      {
        type: "bill_payment",
        id: recordKey,
        timezone: timezone,
      },
      {
        onSettled: () => setDownloadingId(null),
      }
    );
  };

  const columns: ColumnsType<IBillingTransaction> = [
    // {
    //   title: () => (
    //     <p className="font-manrope text-secondary text-[14px] font-medium">
    //       ID
    //     </p>
    //   ),
    //   dataIndex: "id",
    //   className: "w-auto",
    //   key: "id",
    //   render: (value) => (
    //     <div className="flex flex-row gap-2 items-center">
    //       <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
    //         {value}
    //       </p>
    //     </div>
    //   ),
    // },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          User ID
        </p>
      ),
      dataIndex: "userId",
      className: "w-auto",
      key: "userId",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          {canView ? (
            <p
              onClick={() => onSelectUser(value)}
              className="font-manrope text-secondary text-[14px] sm:text-[14px] font-medium underline hover:text-primary hover:underline cursor-pointer"
            >
              {value}
            </p>
          ) : (
            <span className="font-manrope text-secondary text-[14px] sm:text-[14px] font-medium">
              {value}
            </span>
          )}
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Reference
        </p>
      ),
      dataIndex: "payment_reference",
      className: "w-auto",
      key: "payment_reference",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Full Name
        </p>
      ),
      dataIndex: "fullName",
      className: "w-auto",
      key: "fullName",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    // {
    //   title: () => (
    //     <p className="font-manrope text-secondary text-[14px] font-medium">
    //       Email
    //     </p>
    //   ),
    //   dataIndex: "email",
    //   className: "w-auto",
    //   key: "email",
    //   render: (value) => (
    //     <div className="flex flex-row gap-2 items-center">
    //       <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
    //         {value}
    //       </p>
    //     </div>
    //   ),
    // },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Category
        </p>
      ),
      dataIndex: "serviceCategory",
      className: "w-auto",
      key: "serviceCategory",
      render: (value, record) => (
        <div className="flex flex-row gap-2 items-center">
          <SafeImage
            src={record?.serviceCategoryImage}
            alt={value}
            className="w-8 h-8 rounded-full object-cover"
            fallback={
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center"></div>
            }
          />
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium whitespace-nowrap mr-4">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Service
        </p>
      ),
      dataIndex: "serviceName",
      className: "w-auto",
      key: "serviceName",
      render: (value, record) => (
        <div className="flex flex-row gap-2 items-center">
          {record?.serviceImage && (
            <img
              src={record?.serviceImage}
              alt={value}
              className="w-8 h-8 rounded-full object-cover"
            />
          )}
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium ">
            {extractServiceName(value ?? "")}
          </p>
        </div>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Transaction Id
        </p>
      ),
      dataIndex: "transactionId",
      className: "w-auto",
      key: "transactionId",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Account Number
        </p>
      ),
      dataIndex: "accountNumber",
      className: "w-auto",
      key: "accountNumber",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Amount
        </p>
      ),
      dataIndex: "amount",
      className: "w-auto",
      key: "amount",
      render: (value, variables) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}{" "}
            {variables.currency && extractBillingStatus(variables.currency)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Fee
        </p>
      ),
      dataIndex: "fee",
      className: "w-auto",
      key: "fee",
      render: (value, variables) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}{" "}
            {variables.currency && extractBillingStatus(variables.currency)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Benefit
        </p>
      ),
      dataIndex: "benefit",
      className: "w-auto",
      key: "benefit",
      render: (value, variables) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}{" "}
            {variables.currency && extractBillingStatus(variables.currency)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Commission
        </p>
      ),
      dataIndex: "commission",
      className: "w-auto",
      key: "commission",
      render: (value, variables) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}{" "}
            {variables.currency && extractBillingStatus(variables.currency)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Client Commission
        </p>
      ),
      dataIndex: "clientCommission",
      className: "w-auto",
      key: "clientCommission",
      render: (value, variables) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}{" "}
            {variables.currency && extractBillingStatus(variables.currency)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Status
        </p>
      ),
      dataIndex: "status",
      className: "w-auto",
      key: "status",
      render: (value) => {
        const statusClass = getTransactionStatusStyle(
          extractBillingStatus(value)
        );

        return (
          <div className="flex flex-row gap-2 items-center rounded-full">
            <p
              className={`font-manrope text-[12px] font-medium px-2 rounded-full ${statusClass}`}
            >
              {value && extractBillingStatus(value)}
            </p>
          </div>
        );
      },
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Created
        </p>
      ),
      dataIndex: "created_at",
      className: "w-auto",
      key: "created_at",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p
            className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium"
            style={{ whiteSpace: "nowrap" }}
          >
            {/* {moment(value).utc(true).format("DD MMM YYYY H:mm z")} */}
            {/* {moment(value).utcOffset(240).format("DD/MM/YYYY H:mm")} */}
            {moment(value).local().format("DD/MM/YYYY H:mm")}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Receipt
        </p>
      ),
      dataIndex: "receipt",
      className: "w-auto",
      key: "receipt",
      render: (_, record) =>
        extractBillingStatus(record.status) !== "Completed" || !canDownload ? (
          <p className="text-gray-400 text-[12px] font-manrope italic">---</p>
        ) : downloadingId === Number(record.key) ? (
          <p className="text-gray-400 text-[12px] font-manrope italic">
            Downloading...
          </p>
        ) : (
          <a
            onClick={() => receiptBtn(record)}
            className="text-secondary underline hover:text-primary text-[12px] font-manrope font-medium cursor-pointer"
          >
            Receipt
          </a>
        ),
    },
  ];

  const mobileColumns: ColumnsType<IBillingTransaction> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Transaction History
        </p>
      ),
      dataIndex: "action",
      className: "w-auto",
      render: (_, record: { key: React.Key }) => {
        const selData = dataSource.filter((item) => item.key === record.key)[0];

        return (
          <div className="w-full flex flex-row gap-2 justify-between">
            <div className="w-full flex flex-col items-start gap-2">
              <div className="py-[4px]"></div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  UserId
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.userId || "---"}
                </p>
              </div>
              <div className="w-full flex flex-row items-center justify-between gap-2">
                <p className="text-secondary text-[14px] font-manrope font-medium">
                  Reference
                </p>
                <p className="text-primary text-[14px] font-manrope font-medium">
                  {selData ? String(selData.payment_reference) : "-"}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Full Name
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.fullName || "---"}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Category
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.serviceCategory || "---"}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Service
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.serviceName || "---"}
                </p>
              </div>
              <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                Account Number
              </p>
              <p className="text-primary text-[13px] font-manrope font-medium">
                {selData.id}
              </p>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Amount
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.amount || "---"}{" "}
                  {selData.currency && extractBillingStatus(selData.currency)}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Fee
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.fee || "---"}{" "}
                  {selData.currency && extractBillingStatus(selData.currency)}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Benefit
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.benefit}{" "}
                  {selData.currency && extractBillingStatus(selData.currency)}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Commission
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.commission || "---"}{" "}
                  {selData.currency && extractBillingStatus(selData.currency)}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Client Commission
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {selData.clientCommission || "---"}{" "}
                  {selData.currency && extractBillingStatus(selData.currency)}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Status
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {extractBillingStatus(selData.status)}
                </p>
              </div>
              {/* <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Status
                </p>
                <div
                  className={`bg-[${
                    selData.status === "sent_to_iban" ? "#7F8172" : "#E6E8EC"
                  }] inline-block py-[4px] px-[12px] rounded-[10px]`}
                >
                  <p
                    className={`font-manrope ${
                      selData.status === "sent_to_iban"
                        ? "text-[#fff]"
                        : "text-primary"
                    } text-[12px] font-medium leading-none`}
                  >
                    {selData.status &&
                      capitalizeFirstLetter(
                        selData.status.replaceAll("_", " ")
                      )}
                  </p>
                </div>
              </div> */}
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Created
                </p>
                <p className="text-primary text-[13px] font-manrope font-medium">
                  {/* {moment(selData.created_at).format("YYYY-MM-DD HH:mm:ss")} */}
                  {/* {moment
                    .utc(selData.created_at)
                    .utcOffset(240)
                    .format("DD/MM/YYYY H:mm")} */}
                  {moment(selData.created_at).local().format("DD/MM/YYYY H:mm")}
                </p>
              </div>
              <div className="py-[4px]">
                <p className="text-secondary text-[13px] font-manrope font-medium leading-none pb-[3px]">
                  Receipt
                </p>
                {selData.status !== "completed" || !canDownload ? (
                  <p className="text-gray-400 text-[12px] font-manrope italic">
                    ---
                  </p>
                ) : downloadingId === Number(selData.key) ? (
                  <p className="text-gray-400 text-[12px] font-manrope italic">
                    Downloading...
                  </p>
                ) : (
                  <p
                    onClick={() => receiptBtn(Number(selData.key))}
                    className="text-secondary underline hover:text-primary text-[12px] font-manrope font-medium cursor-pointer"
                  >
                    Receipt
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
  ];

  if (!canViewTransactions) {
    return (
      <div className="custom-scroll bg-white rounded-[12px] w-full p-6 text-center text-secondary">
        You do not have permission to view transactions.
      </div>
    );
  }

  return (
    <div className="custom-scroll bg-white rounded-[12px] w-full">
      <div className="p-4 relative">
        <Table
          loading={isLoading}
          showSorterTooltip={false}
          columns={isMobile ? mobileColumns : columns}
          // columns={columns}
          dataSource={dataSource}
          pagination={{
            total,
            current: searchParams.current_page,
            onChange: onPageChange,
            position: ["bottomCenter"],
            defaultPageSize: LIST_PAGE_SIZE,
            showSizeChanger: false,
          }}
        />
      </div>
    </div>
  );
};

export default BillingsListPage;
